// VoiceWaveform.jsx
//
// Bar-style equalizer visualisation for the candidate's live mic input.
// Consumes the existing AnalyserNode (via analyserRef) — no extra
// getUserMedia call, no extra CPU beyond a 60fps canvas draw while the
// candidate is actively recording.
//
// Draws only when `active` is true, so we don't burn battery between
// questions (the raf loop is torn down and re-armed on active toggle).

import React, { useEffect, useRef } from 'react';

const DEFAULT_BAR_COUNT = 18;
const DEFAULT_BAR_GAP = 2;

function VoiceWaveform({
  analyserRef,
  active = false,
  width = 120,
  height = 28,
  barCount = DEFAULT_BAR_COUNT,
  barGap = DEFAULT_BAR_GAP,
  color = 'rgb(34, 197, 94)', // tailwind green-500
  className = '',
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    if (!active) {
      // Cancel any pending frame + clear the canvas so the last drawn
      // waveform doesn't linger after recording ends.
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return undefined;
    }

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const barWidth = (width - barGap * (barCount - 1)) / barCount;

    const draw = () => {
      const analyser = analyserRef?.current;
      if (!analyser) {
        // Analyser may not be ready yet (first render of a new question).
        // Keep the loop alive so we pick it up on the next frame.
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      if (!dataArrayRef.current || dataArrayRef.current.length !== analyser.frequencyBinCount) {
        dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
      }
      analyser.getByteFrequencyData(dataArrayRef.current);

      ctx.clearRect(0, 0, width, height);

      // Sample `barCount` bands evenly across the useful frequency range
      // (skip the very top — it's mostly noise).
      const usableBins = Math.floor(analyser.frequencyBinCount * 0.6);
      const binsPerBar = Math.floor(usableBins / barCount);

      for (let i = 0; i < barCount; i++) {
        // Average the bins that fall into this bar for a smoother look.
        let sum = 0;
        for (let j = 0; j < binsPerBar; j++) {
          sum += dataArrayRef.current[i * binsPerBar + j];
        }
        const avg = sum / binsPerBar;
        // Normalise 0-255 → 0-1, apply small floor so quiet frames still
        // show a visible tick (helps the widget feel alive at low input).
        const normalized = Math.max(0.06, avg / 255);
        const barHeight = normalized * height;
        const x = i * (barWidth + barGap);
        const y = (height - barHeight) / 2;

        ctx.fillStyle = color;
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [active, width, height, barCount, barGap, color, analyserRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className={className}
      aria-hidden="true"
    />
  );
}

export default VoiceWaveform;
